import { useCallback, useRef, useState, useEffect } from "react";
import throttle from "lodash.throttle";
import "./WheelPicker.scss";

interface Column {
  items: string[];
}

interface WheelPickerProps {
  columns: Column[];
  onChange: (values: string[]) => void;
  initialValues?: string[];
}

export const WheelPicker: React.FC<WheelPickerProps> = ({
  columns,
  onChange,
  initialValues,
}) => {
  const [selectedIndices, setSelectedIndices] = useState<number[]>(
    initialValues?.map((val, i) => columns[i]?.items.indexOf(val)) ||
      Array(columns.length).fill(0)
  );

  const selectedIndicesRef = useRef(selectedIndices);
  selectedIndicesRef.current = selectedIndices;

  const listsRef = useRef<(HTMLUListElement | null)[]>([]);
  const itemHeight = 40;
  const windowSize = 10;

  useEffect(() => {
    const values = selectedIndices.map(
      (index, colIndex) => columns[colIndex].items[index]
    );
    onChange(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateItemRotation = useCallback(
    throttle((columnIndex: number): void => {
      const list = listsRef.current[columnIndex];
      if (!list) return;

      const items = Array.from(list.children);
      const listRect = list.getBoundingClientRect();
      const centerY = listRect.top + listRect.height / 2;

      const scrollTop = Math.round(list.scrollTop);
      const centerIndex = Math.round(scrollTop / itemHeight);

      const startIndex = Math.max(centerIndex - windowSize, 0);
      const endIndex = Math.min(centerIndex + windowSize, items.length - 1);

      for (let i = startIndex; i <= endIndex; i++) {
        const item = items[i];
        if (!item || item.classList.contains("wheel-picker-spacer")) continue;

        const itemElement = item as HTMLElement;
        const itemRect = itemElement.getBoundingClientRect();
        const itemCenterY = itemRect.top + itemRect.height / 2;
        const distanceFromCenter = itemCenterY - centerY;
        const rotation = (distanceFromCenter / itemHeight) * -20;
        itemElement.style.setProperty("--rotation", `${rotation}deg`);
      }
    }, 16),
    [itemHeight, windowSize]
  );

  const throttledOnChange = useRef(
    throttle((newIndices: number[]): void => {
      const values = newIndices.map(
        (index, colIndex) => columns[colIndex].items[index]
      );
      onChange(values);
    }, 100)
  ).current;

  const handleScroll = useCallback(
    (columnIndex: number) => {
      const list = listsRef.current[columnIndex];
      if (!list) return;

      const scrollHandler = (): void => {
        const scrollTop = Math.round(list.scrollTop);
        const index = Math.round(scrollTop / itemHeight);

        if (selectedIndicesRef.current[columnIndex] !== index) {
          const newIndices = [...selectedIndicesRef.current];
          newIndices[columnIndex] = index;
          setSelectedIndices(newIndices);
          throttledOnChange(newIndices);
        }

        updateItemRotation(columnIndex);
      };

      return throttle(scrollHandler, 16);
    },
    [throttledOnChange, updateItemRotation, itemHeight]
  );

  const handleItemClick = useCallback(
    (columnIndex: number, index: number) => {
      const list = listsRef.current[columnIndex];
      if (!list) return;

      list.scrollTo({
        top: index * itemHeight,
        behavior: "smooth",
      });

      if (selectedIndicesRef.current[columnIndex] !== index) {
        const newIndices = [...selectedIndicesRef.current];
        newIndices[columnIndex] = index;
        setSelectedIndices(newIndices);
        throttledOnChange(newIndices);
      }
    },
    [throttledOnChange, itemHeight]
  );

  useEffect(() => {
    const scrollHandlers: Array<() => void> = [];

    columns.forEach((_, columnIndex) => {
      const list = listsRef.current[columnIndex];
      if (list) {
        const expectedTop = selectedIndices[columnIndex] * itemHeight;
        list.scrollTop = expectedTop;
        updateItemRotation(columnIndex);

        const scrollHandler = handleScroll(columnIndex);
        // @ts-ignore
        scrollHandlers[columnIndex] = scrollHandler;
        // @ts-ignore
        list.addEventListener("scroll", scrollHandler);
      }
    });
    // keep a reference to the listsRef because in the cleanup function, listsRef will be null
    // and that means we can't remove the event listeners
    // and performance will be affected this way
    const savedListRefs = listsRef;
    return () => {
      columns.forEach((_, columnIndex) => {
        const list = savedListRefs.current[columnIndex];
        if (list) {
          const scrollHandler = scrollHandlers[columnIndex];
          if (scrollHandler) {
            list.removeEventListener("scroll", scrollHandler);
          }
        }
      });
      throttledOnChange.cancel();
      updateItemRotation.cancel();
    };
  }, [
    columns,
    selectedIndices,
    handleScroll,
    updateItemRotation,
    throttledOnChange,
    itemHeight,
  ]);

  return (
    <div className="wheel-picker">
      {columns.map((column, columnIndex) => (
        <div key={columnIndex} className="wheel-picker-column">
          <ul ref={(el) => (listsRef.current[columnIndex] = el)}>
            <li
              className="wheel-picker-spacer"
              style={{ height: `${2 * itemHeight}px` }}
            />
            {column.items.map((item, index) => (
              <li
                key={index}
                className={`wheel-picker-item ${
                  selectedIndices[columnIndex] === index ? "selected" : ""
                }`}
                style={{
                  height: `${itemHeight}px`,
                }}
                onClick={() => handleItemClick(columnIndex, index)}
              >
                {item}
              </li>
            ))}
            <li
              className="wheel-picker-spacer"
              style={{ height: `${2 * itemHeight}px` }}
            />
          </ul>
        </div>
      ))}
    </div>
  );
};
